import { useEffect, useRef, useState } from 'react';
import { CheckIcon, Cog6ToothIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';

export default function ThemeSettings() {
  const {
    theme,
    fontFamily,
    fontSize,
    density,
    setTheme,
    setFontFamily,
    setFontSize,
    setDensity,
    resetAppearance,
    THEME_OPTIONS,
    FONT_OPTIONS,
    FONT_SIZE_OPTIONS,
    DENSITY_OPTIONS,
  } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="theme-settings-trigger"
        title="Appearance settings"
        aria-label="Appearance settings"
        aria-expanded={open}
      >
        <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />
      </button>

      {open && (
        <div className="theme-settings-panel" role="dialog" aria-label="Appearance settings">
          <div className="theme-settings-header">
            <div>
              <p className="theme-settings-title">Appearance</p>
              <p className="theme-settings-subtitle">Choose your workspace style</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="theme-settings-close"
              title="Close"
              aria-label="Close appearance settings"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="theme-settings-body">
            <fieldset>
              <legend className="theme-settings-label">Interface theme</legend>
              <div className="theme-option-grid">
                {THEME_OPTIONS.map((option) => {
                  const selected = theme === option.id;
                  return (
                    <button
                      type="button"
                      key={option.id}
                      onClick={() => setTheme(option.id)}
                      className={`theme-option${selected ? ' selected' : ''}`}
                      aria-pressed={selected}
                    >
                      <span className="theme-option-preview" aria-hidden="true">
                        <span style={{ backgroundColor: option.preview[0] }} />
                        <span style={{ backgroundColor: option.preview[1] }} />
                        <span style={{ backgroundColor: option.preview[2] }} />
                      </span>
                      <span className="theme-option-copy">
                        <span className="theme-option-name">{option.label}</span>
                        <span className="theme-option-description">{option.description}</span>
                      </span>
                      {selected && <CheckIcon className="theme-option-check" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div className="theme-settings-row">
              <label htmlFor="appearance-density" className="theme-settings-label">Density</label>
              <div className="theme-segmented" id="appearance-density">
                {DENSITY_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.id}
                    onClick={() => setDensity(option.id)}
                    className={density === option.id ? 'active' : ''}
                    aria-pressed={density === option.id}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="theme-settings-selects">
              <label>
                <span className="theme-settings-label">Typography</span>
                <select value={fontFamily} onChange={(event) => setFontFamily(event.target.value)} className="input">
                  {FONT_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
              </label>
              <label>
                <span className="theme-settings-label">Text size</span>
                <select value={fontSize} onChange={(event) => setFontSize(event.target.value)} className="input">
                  {FONT_SIZE_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
              </label>
            </div>
          </div>

          <div className="theme-settings-footer">
            <button type="button" onClick={resetAppearance} className="theme-reset-button">Reset to default</button>
          </div>
        </div>
      )}
    </div>
  );
}
